import { useCallback, useContext, useState } from "react";
import { toast } from "react-hot-toast";
import { GlobalContext } from "../../../Context/GlobalContext";
import { uploadItemImage } from "../../../Utils/imageUploadUtils";

const useChaafo = () => {
  const {
    api,
    anlookApi,
    adminId,
    currentAdmin,
    userID: contextUserID,
  } = useContext(GlobalContext);

  const userID =
    contextUserID ||
    currentAdmin?.userid ||
    currentAdmin?.userId ||
    adminId ||
    "";

  const [loading, setLoading] = useState(false);
  const [restaurantsByCategory, setRestaurantsByCategory] = useState([]);
  const [chaafoCategories, setChaafoCategories] = useState([]);
  const [restaurantCategories, setRestaurantCategories] = useState([]);
  const [bannerDetails, setBannerDetails] = useState([]);
  const [bannerLoading, setBannerLoading] = useState(false);
  const [bannerError, setBannerError] = useState("");

  const [messageConfigLoading, setMessageConfigLoading] = useState(false);
  const [loginGatewayLoading, setLoginGatewayLoading] = useState(false);

  // ================= CATALOG HIGHLIGHTS STATE =================
  const [catalogFeatureLoading, setCatalogFeatureLoading] = useState(false);
  const [catalogMenus, setCatalogMenus] = useState([]);
  const [recommendedItems, setRecommendedItems] = useState([]);
  const [offerItems, setOfferItems] = useState([]);
  const [suggestionItems, setSuggestionItems] = useState([]);

  const cleanFileNameText = (value = "") => {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/%/g, "percent")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  };

  const cleanImageUrl = (value = "") => {
    return String(value || "")
      .trim()
      .replace(/\s+/g, "");
  };

  const normalizeServingType = (value = "") => {
    const normalizedValue = String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, "");

    if (["v", "veg", "vegetarian"].includes(normalizedValue)) {
      return "V";
    }

    if (["n", "nonveg", "nonvegetarian"].includes(normalizedValue)) {
      return "N";
    }

    if (["b", "both", "all", "vegandnonveg"].includes(normalizedValue)) {
      return "B";
    }

    return String(value || "")
      .trim()
      .toUpperCase();
  };

  const insertRestaurantViaCategory = async ({
    userId,
    categoryId,
    restaurantDescription,
    restaurantProfileImage,
    restaurantAddress,
    restaurantOffer,
    servingType,
    ratings,
    startingPrice,
  }) => {
    try {
      const selectedUserId = String(userId || "").trim();

      if (!selectedUserId) {
        toast.error("Selected client user ID not found");
        console.error("INSERT RESTAURANT: SELECTED CLIENT USER ID MISSING", {
          userId,
        });
        return null;
      }

      setLoading(true);

      const requestBody = {
        user_id: selectedUserId,
        category_id: String(categoryId || ""),
        restaurant_description: String(restaurantDescription || ""),
        restaurant_profile_image: String(restaurantProfileImage || ""),
        restaurant_address: String(restaurantAddress || ""),
        restaurant_offer: String(restaurantOffer || ""),
        serving_type: normalizeServingType(servingType),
        ratings: String(ratings || ""),
        starting_price: String(startingPrice || ""),
      };

      console.log("INSERT RESTAURANT VIA CATEGORY PAYLOAD:", requestBody);

      const response = await api.post(
        "/Chaafo/restaurantInsertViaCategory",
        requestBody,
      );

      if (response?.data?.responseCode === "00") {
        toast.success(
          response?.data?.responseMessage || "Restaurant added successfully",
        );

        return response.data;
      }

      toast.error(
        response?.data?.responseMessage || "Failed to add restaurant",
      );

      return null;
    } catch (error) {
      console.error("INSERT RESTAURANT VIA CATEGORY ERROR:", error);

      toast.error(
        error?.response?.data?.responseMessage || "Something went wrong",
      );

      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchRestaurantViaCategory = async ({
    categoryIds = [],
    categoryId,
    servingType = "",
    limit = 10,
    offset = 0,
    customerLatitude = "",
    customerLongitude = "",
    range = 10,
  } = {}) => {
    try {
      setLoading(true);

      const rawCategoryIds =
        categoryId !== undefined && categoryId !== null
          ? categoryId
          : categoryIds;

      const normalizedCategoryIds = (
        Array.isArray(rawCategoryIds) ? rawCategoryIds : [rawCategoryIds]
      ).map((id) => String(id ?? "").trim());

      const requestBody = {
        category_id:
          normalizedCategoryIds.length > 0 ? normalizedCategoryIds : [""],
        serving_type: normalizeServingType(servingType),
        limit: Number.isFinite(Number(limit)) ? Number(limit) : 10,
        offset: Number.isFinite(Number(offset)) ? Number(offset) : 0,
        customerLatitude: String(customerLatitude ?? ""),
        customerLongitude: String(customerLongitude ?? ""),
        range: Number.isFinite(Number(range)) ? Number(range) : 10,
      };

      console.log("FETCH RESTAURANT VIA CATEGORY V1 PAYLOAD:", requestBody);

      const response = await api.post(
        "/Chaafo/restaurantGetViaCategoryV1",
        requestBody,
      );

      if (response?.data?.responseCode === "00") {
        const restaurantList = Array.isArray(response?.data?.response)
          ? response.data.response
          : [];

        setRestaurantsByCategory(restaurantList);

        return response.data;
      }

      setRestaurantsByCategory([]);

      toast.error(
        response?.data?.responseMessage || "Failed to fetch restaurants",
      );

      return null;
    } catch (error) {
      console.error("FETCH RESTAURANT VIA CATEGORY V1 ERROR:", error);

      setRestaurantsByCategory([]);

      toast.error(
        error?.response?.data?.responseMessage || "Something went wrong",
      );

      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateRestaurantViaCategory = async ({
    userId,
    restaurantId,
    categoryId,
    restaurantName,
    restaurantDescription,
    restaurantProfileImage,
    restaurantAddress,
    restaurantOffer,
    servingType,
    ratings,
    startingPrice,
    activeStatus = "Y",
  }) => {
    try {
      const selectedUserId = String(userId || "").trim();

      if (!selectedUserId) {
        toast.error("Selected client user ID not found");

        console.error("UPDATE RESTAURANT USER ID MISSING:", {
          userId,
        });

        return null;
      }

      setLoading(true);

      const requestBody = {
        pk_restaurant_id: String(restaurantId || ""),
        fk_category_id: String(categoryId || ""),

        // Selected dropdown client userid
        user_id: selectedUserId,

        restaurant_name: String(restaurantName || ""),
        restaurant_description: String(restaurantDescription || ""),
        restaurant_profile_image: String(restaurantProfileImage || ""),
        restaurant_address: String(restaurantAddress || ""),
        restaurant_offer: String(restaurantOffer || ""),
        serving_type: normalizeServingType(servingType),
        ratings: String(ratings || ""),
        starting_price: String(startingPrice || ""),
        active_status:
          String(activeStatus || "Y")
            .trim()
            .toUpperCase() === "N"
            ? "N"
            : "Y",
      };

      console.log("UPDATE RESTAURANT FINAL PAYLOAD:", requestBody);

      const response = await api.post(
        "/Chaafo/restaurantUpdateViaCategory",
        requestBody,
      );

      if (response?.data?.responseCode === "00") {
        toast.success(
          response?.data?.responseMessage || "Restaurant updated successfully",
        );

        return response.data;
      }

      toast.error(
        response?.data?.responseMessage || "Failed to update restaurant",
      );

      return null;
    } catch (error) {
      console.error("UPDATE RESTAURANT VIA CATEGORY ERROR:", error);

      toast.error(
        error?.response?.data?.responseMessage || "Something went wrong",
      );

      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchRestaurantCategories = async (servingType = "") => {
    try {
      setLoading(true);

      const requestBody = {
        serving_type: normalizeServingType(servingType),
      };

      const response = await api.post(
        "/Chaafo/restaurantCategoryGet",
        requestBody,
      );

      if (response?.data?.responseCode === "00") {
        setRestaurantCategories(response?.data?.response || []);

        return response.data;
      }

      setRestaurantCategories([]);

      toast.error(
        response?.data?.responseMessage ||
          "Failed to fetch restaurant categories",
      );

      return null;
    } catch (error) {
      console.error("FETCH RESTAURANT CATEGORIES ERROR:", error);

      setRestaurantCategories([]);

      toast.error(
        error?.response?.data?.responseMessage || "Something went wrong",
      );

      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteRestaurantViaCategory = async (restaurantId) => {
    try {
      setLoading(true);

      const requestBody = {
        restaurant_id: String(restaurantId || ""),
      };

      const response = await api.post(
        "/Chaafo/restaurantDeleteViaCategory",
        requestBody,
      );

      if (response?.data?.responseCode === "00") {
        toast.success(
          response?.data?.responseMessage || "Restaurant deleted successfully",
        );

        return response.data;
      }

      toast.error(
        response?.data?.responseMessage || "Failed to delete restaurant",
      );

      return null;
    } catch (error) {
      console.error("DELETE RESTAURANT VIA CATEGORY ERROR:", error);

      toast.error(
        error?.response?.data?.responseMessage || "Something went wrong",
      );

      return null;
    } finally {
      setLoading(false);
    }
  };

  const uploadChaafoImage = async ({
    file,
    itemName = "chaafo_image",
    selectedUserId = "",
  }) => {
    try {
      if (!file) {
        toast.error("Please select image");
        return null;
      }

      const uploadUserId = String(selectedUserId || userID || "").trim();

      if (!uploadUserId) {
        toast.error("User ID missing");
        return null;
      }

      const safeItemName =
        cleanFileNameText(itemName) ||
        cleanFileNameText(file.name) ||
        "chaafo_image";

      const result = await uploadItemImage({
        anlookApi,
        userid: uploadUserId,
        itemName: safeItemName,
        file,
        maxSizeMB: 5,
      });

      if (!result?.imageUrl) {
        throw new Error("Image URL missing");
      }

      return {
        ...(result?.response || {}),
        image_url: result.imageUrl,
        image_name: result.imageName || "",
      };
    } catch (error) {
      console.error("CHAAFO IMAGE UPLOAD ERROR:", error);

      toast.error(error?.message || "Image upload failed");

      return null;
    }
  };

  const insertChaafoCategory = async ({
    categoryName,
    categoryDescription,
    categoryImage,
  }) => {
    try {
      setLoading(true);

      const requestBody = {
        category_name: String(categoryName || ""),
        category_description: String(categoryDescription || ""),
        category_image: String(categoryImage || ""),
      };

      const response = await api.post("/Chaafo/categoryInsert", requestBody);

      if (response?.data?.responseCode === "00") {
        toast.success(
          response?.data?.responseMessage || "Category added successfully",
        );

        return response.data;
      }

      toast.error(response?.data?.responseMessage || "Failed to add category");

      return null;
    } catch (error) {
      console.error("INSERT CHAAFO CATEGORY ERROR:", error);

      toast.error(
        error?.response?.data?.responseMessage || "Something went wrong",
      );

      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchChaafoCategories = useCallback(
    async ({ limit = 100, offset = 0, type } = {}) => {
      try {
        setLoading(true);

        const normalizedType =
          type === 0 || type === "0" ? 0 : type === 1 || type === "1" ? 1 : "";

        const requestBody = {
          limit: Number.isFinite(Number(limit)) ? Number(limit) : 100,
          offset: Number.isFinite(Number(offset)) ? Number(offset) : 0,
          type: normalizedType,
        };

        console.log("FETCH CHAAFO CATEGORIES PAYLOAD:", requestBody);

        const response = await api.post("/Chaafo/categoryGet", requestBody);

        const responseData = response?.data;

        if (String(responseData?.responseCode) !== "00") {
          setChaafoCategories([]);

          toast.error(
            responseData?.responseMessage || "Failed to fetch categories",
          );

          return null;
        }

        let categoryList = Array.isArray(responseData?.response)
          ? responseData.response
          : [];

        // ===========================
        // ALL CATEGORY SORT FIX
        // ===========================
        const allCategory = categoryList.find(
          (item) =>
            String(item?.category_name || "")
              .trim()
              .toLowerCase() === "all",
        );

        const allCategoryId = Number(
          allCategory?.pk_category_id ??
            allCategory?.category_id ??
            allCategory?.id,
        );

        const allSortOrder =
          allCategory?.sort_order ??
          allCategory?.item_sort_order ??
          allCategory?.sortOrder ??
          allCategory?.itemSortOrder;

        const shouldFixAllSortOrder =
          allCategory &&
          Number.isFinite(allCategoryId) &&
          (allSortOrder === null ||
            allSortOrder === undefined ||
            String(allSortOrder).trim() === "");

        if (shouldFixAllSortOrder) {
          const sortUpdatePayload = {
            category_id: [allCategoryId],
            sortOrder: [1],
          };

          const sortUpdateResponse = await api.post(
            "/Chaafo/updateCategorySortOrder",
            sortUpdatePayload,
          );

          if (String(sortUpdateResponse?.data?.responseCode) === "00") {
            // Same type/limit/offset ke saath refresh
            const refreshedResponse = await api.post(
              "/Chaafo/categoryGet",
              requestBody,
            );

            if (String(refreshedResponse?.data?.responseCode) === "00") {
              categoryList = Array.isArray(refreshedResponse?.data?.response)
                ? refreshedResponse.data.response
                : [];
            }
          }
        }

        setChaafoCategories(categoryList);

        return {
          ...responseData,
          response: categoryList,
          success: true,
        };
      } catch (error) {
        console.error(
          "FETCH CHAAFO CATEGORIES ERROR:",
          error?.response?.data || error,
        );

        setChaafoCategories([]);

        toast.error(
          error?.response?.data?.responseMessage || "Something went wrong",
        );

        return null;
      } finally {
        setLoading(false);
      }
    },
    [api],
  );

  const updateChaafoCategory = async ({
    categoryId,
    categoryName,
    categoryDescription,
    categoryImage,
    activeStatus = "Y",
  }) => {
    try {
      setLoading(true);

      const requestBody = {
        pk_category_id: String(categoryId || ""),
        category_name: String(categoryName || ""),
        category_description: String(categoryDescription || ""),
        category_image: String(categoryImage || ""),

        active_status:
          String(activeStatus || "Y")
            .trim()
            .toUpperCase() === "N"
            ? "N"
            : "Y",
      };

      console.log("UPDATE CHAAFO CATEGORY PAYLOAD:", requestBody);

      const response = await api.post("/Chaafo/categoryUpdate", requestBody);

      if (response?.data?.responseCode === "00") {
        toast.success(
          response?.data?.responseMessage || "Category updated successfully",
        );

        return response.data;
      }

      toast.error(
        response?.data?.responseMessage || "Failed to update category",
      );

      return null;
    } catch (error) {
      console.error("UPDATE CHAAFO CATEGORY ERROR:", error);

      toast.error(
        error?.response?.data?.responseMessage || "Something went wrong",
      );

      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateChaafoCategorySortOrder = async ({
    categoryIds = [],
    sortOrders = [],
  } = {}) => {
    try {
      const rawCategoryIds = Array.isArray(categoryIds)
        ? categoryIds
        : [categoryIds];

      const rawSortOrders = Array.isArray(sortOrders)
        ? sortOrders
        : [sortOrders];

      if (!rawCategoryIds.length) {
        toast.error("Category ID missing");
        return null;
      }

      if (!rawSortOrders.length) {
        toast.error("Sort order missing");
        return null;
      }

      if (rawCategoryIds.length !== rawSortOrders.length) {
        toast.error("Category and sort order count must be same");
        return null;
      }

      const normalizedCategoryIds = [];
      const normalizedSortOrders = [];

      for (let index = 0; index < rawCategoryIds.length; index += 1) {
        const categoryId = Number(rawCategoryIds[index]);
        const sortOrder = Number(rawSortOrders[index]);

        if (!Number.isFinite(categoryId)) {
          toast.error(`Invalid category ID at row ${index + 1}`);
          return null;
        }

        if (!Number.isInteger(sortOrder) || sortOrder < 0) {
          toast.error(`Invalid sort order at row ${index + 1}`);
          return null;
        }

        normalizedCategoryIds.push(categoryId);
        normalizedSortOrders.push(sortOrder);
      }

      setLoading(true);

      const requestBody = {
        category_id: normalizedCategoryIds,
        sortOrder: normalizedSortOrders,
      };

      console.log("UPDATE CATEGORY SORT ORDER PAYLOAD:", requestBody);

      const response = await api.post(
        "/Chaafo/updateCategorySortOrder",
        requestBody,
      );

      if (response?.data?.responseCode === "00") {
        toast.success(
          response?.data?.responseMessage ||
            "Category sort order updated successfully",
        );

        return response.data;
      }

      toast.error(
        response?.data?.responseMessage ||
          "Failed to update category sort order",
      );

      return null;
    } catch (error) {
      console.error("UPDATE CATEGORY SORT ORDER ERROR:", error);

      toast.error(
        error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          "Something went wrong",
      );

      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteChaafoCategory = async (categoryId) => {
    try {
      setLoading(true);

      const requestBody = {
        pk_category_id: String(categoryId || ""),
      };

      const response = await api.post("/Chaafo/categoryDelete", requestBody);

      if (response?.data?.responseCode === "00") {
        toast.success(
          response?.data?.responseMessage || "Category deleted successfully",
        );

        return response.data;
      }

      toast.error(
        response?.data?.responseMessage || "Failed to delete category",
      );

      return null;
    } catch (error) {
      console.error("DELETE CHAAFO CATEGORY ERROR:", error);

      toast.error(
        error?.response?.data?.responseMessage || "Something went wrong",
      );

      return null;
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // CHAAFO BANNER INFO - NEW API
  // =========================================================

  const getChaafoApiMessage = (data, fallbackMessage) => {
    return (
      data?.response?.[0]?.message || data?.responseMessage || fallbackMessage
    );
  };

  const isChaafoMutationSuccess = (data) => {
    const firstResponse = Array.isArray(data?.response)
      ? data.response[0]
      : null;

    return String(data?.responseCode) === "00" && firstResponse?.id === "Y";
  };

  const fetchBannerDetails = async ({
    customerId = "",
    customer_id = "",
    platform = "",
    plateform = "",
    ratio = "",
    shopCode = "",
    shop_code = "",
    type = "",
    bannerType = "",
    banner_type = "",
  } = {}) => {
    try {
      setBannerLoading(true);
      setBannerError("");

      const requestBody = {
        customer_id: String(customerId || customer_id || ""),
        in_latitude: "",
        in_longitude: "",
        plateform: String(platform || plateform || ""),
        ratio: String(ratio || ""),
        shop_code: String(shopCode || shop_code || ""),
        type: String(type || bannerType || banner_type || ""),
      };

      console.log("GET CHAAFO BANNER INFO PAYLOAD:", requestBody);

      const response = await api.post(
        "/Chaafo/getChaafoBannerInfo",
        requestBody,
      );

      const responseData = response?.data;

      if (String(responseData?.responseCode) !== "00") {
        const message =
          responseData?.responseMessage || "Unable to fetch banners.";

        setBannerDetails([]);
        setBannerError(message);

        return {
          success: false,
          data: [],
          message,
          response: responseData,
        };
      }

      const bannerList = Array.isArray(responseData?.response)
        ? responseData.response
        : [];

      setBannerDetails(bannerList);

      return {
        success: true,
        data: bannerList,
        response: responseData,
      };
    } catch (error) {
      console.error(
        "GET CHAAFO BANNER INFO ERROR:",
        error?.response?.data || error,
      );

      const message =
        error?.response?.data?.responseMessage ||
        error?.response?.data?.message ||
        error?.message ||
        "Unable to fetch banners.";

      setBannerDetails([]);
      setBannerError(message);

      return {
        success: false,
        data: [],
        message,
      };
    } finally {
      setBannerLoading(false);
    }
  };

  const buildBannerMutationPayload = (payload = {}) => {
    return {
      shop_code: String(
        payload.shop_code ||
          payload.shopCode ||
          payload.fkUserId ||
          payload.userId ||
          "",
      ).trim(),

      banner_type: String(
        payload.banner_type || payload.bannerType || payload.offer || "",
      ).trim(),

      aspect_ratio: String(
        payload.aspect_ratio || payload.aspectRatio || payload.ratio || "16:9",
      ).trim(),

      plateform: String(payload.plateform || payload.platform || "both").trim(),

      on_tap: String(
        payload.on_tap || payload.onTap || payload.desc || "",
      ).trim(),

      banner_image_url: cleanImageUrl(
        payload.banner_image_url ||
          payload.bannerImageUrl ||
          payload.imageUrl ||
          "",
      ),
    };
  };

  const insertBannerDetails = async (payload = {}) => {
    try {
      const requestBody = buildBannerMutationPayload(payload);

      if (!requestBody.shop_code) {
        toast.error("Shop code is missing");
        return null;
      }

      if (!requestBody.banner_type) {
        toast.error("Please enter banner type");
        return null;
      }

      if (!requestBody.banner_image_url) {
        toast.error("Please select banner image");
        return null;
      }

      setLoading(true);

      console.log("INSERT CHAAFO BANNER INFO PAYLOAD:", requestBody);

      const response = await api.post(
        "/Chaafo/insertChaafoBannerInfo",
        requestBody,
      );

      if (isChaafoMutationSuccess(response?.data)) {
        toast.success(
          getChaafoApiMessage(response.data, "Banner added successfully"),
        );

        return response.data;
      }

      toast.error(getChaafoApiMessage(response?.data, "Failed to add banner"));

      return null;
    } catch (error) {
      console.error("INSERT CHAAFO BANNER INFO ERROR:", error);

      toast.error(
        error?.response?.data?.responseMessage || "Something went wrong",
      );

      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateBannerDetails = async (payload = {}) => {
    try {
      const bannerId = String(
        payload.id || payload.pk_banner_id || payload.bannerId || "",
      ).trim();

      if (!bannerId) {
        toast.error("Banner ID is missing");
        return null;
      }

      const requestBody = {
        id: bannerId,
        ...buildBannerMutationPayload(payload),
      };

      if (!requestBody.shop_code) {
        toast.error("Shop code is missing");
        return null;
      }

      if (!requestBody.banner_type) {
        toast.error("Please enter banner type");
        return null;
      }

      if (!requestBody.banner_image_url) {
        toast.error("Please select banner image");
        return null;
      }

      setLoading(true);

      console.log("UPDATE CHAAFO BANNER INFO PAYLOAD:", requestBody);

      const response = await api.post(
        "/Chaafo/updateChaafoBannerInfo",
        requestBody,
      );

      if (isChaafoMutationSuccess(response?.data)) {
        toast.success(
          getChaafoApiMessage(response.data, "Banner updated successfully"),
        );

        return response.data;
      }

      toast.error(
        getChaafoApiMessage(response?.data, "Failed to update banner"),
      );

      return null;
    } catch (error) {
      console.error("UPDATE CHAAFO BANNER INFO ERROR:", error);

      toast.error(
        error?.response?.data?.responseMessage || "Something went wrong",
      );

      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteBannerDetails = async (id) => {
    try {
      const bannerId =
        typeof id === "object"
          ? String(id?.id || id?.pk_banner_id || id?.bannerId || "").trim()
          : String(id || "").trim();

      if (!bannerId) {
        toast.error("Banner ID is missing");
        return null;
      }

      setLoading(true);

      const requestBody = {
        id: bannerId,
      };

      console.log("DELETE CHAAFO BANNER INFO PAYLOAD:", requestBody);

      const response = await api.post(
        "/Chaafo/deleteChaafoBannerInfo",
        requestBody,
      );

      if (isChaafoMutationSuccess(response?.data)) {
        toast.success(
          getChaafoApiMessage(response.data, "Banner deleted successfully"),
        );

        return response.data;
      }

      toast.error(
        getChaafoApiMessage(response?.data, "Failed to delete banner"),
      );

      return null;
    } catch (error) {
      console.error("DELETE CHAAFO BANNER INFO ERROR:", error);

      toast.error(
        error?.response?.data?.responseMessage || "Something went wrong",
      );

      return null;
    } finally {
      setLoading(false);
    }
  };
  // =========================================================
  // CATALOG RECOMMENDED, OFFERS AND SUGGESTIONS
  // =========================================================

  const fetchCatalogRecommendedAndOffers = async (selectedUserId) => {
    try {
      const clientUserId = String(selectedUserId || "").trim();

      if (!clientUserId) {
        toast.error("Selected client user ID not found");
        return null;
      }

      setCatalogFeatureLoading(true);

      const response = await api.post(
        "/Order/getCatalogItemsRecommendedAndOffers",
        {
          user_id: clientUserId,
        },
      );

      if (response?.data?.responseCode === "00") {
        const responseData = response?.data?.response || {};

        setCatalogMenus(
          Array.isArray(responseData?.menus) ? responseData.menus : [],
        );

        setRecommendedItems(
          Array.isArray(responseData?.recommended_items)
            ? responseData.recommended_items
            : [],
        );

        setOfferItems(
          Array.isArray(responseData?.offers) ? responseData.offers : [],
        );

        return response.data;
      }

      setCatalogMenus([]);
      setRecommendedItems([]);
      setOfferItems([]);

      toast.error(
        response?.data?.responseMessage || "Failed to fetch catalog items",
      );

      return null;
    } catch (error) {
      console.error("FETCH CATALOG RECOMMENDED/OFFERS ERROR:", error);

      setCatalogMenus([]);
      setRecommendedItems([]);
      setOfferItems([]);

      toast.error(
        error?.response?.data?.responseMessage || "Something went wrong",
      );

      return null;
    } finally {
      setCatalogFeatureLoading(false);
    }
  };

  const fetchCatalogSuggestions = async ({
    selectedUserId = "",
    userId = "",
    userid = "",
    orderType = "home_delivery",
    platform = "string",
  } = {}) => {
    try {
      const clientUserId = String(
        selectedUserId || userId || userid || "",
      ).trim();

      if (!clientUserId) {
        toast.error("Selected client user ID not found");
        return null;
      }

      setCatalogFeatureLoading(true);

      const requestBody = {
        userid: clientUserId,
        orderType: String(orderType || "home_delivery"),
        platform: String(platform || "string"),
      };

      console.log("FETCH CATALOG ITEMS V2 PAYLOAD:", requestBody);

      const response = await api.post(
        "/Order/v2.0/getCatalogItems",
        requestBody,
      );

      if (response?.data?.responseCode === "00") {
        const responseData = response?.data?.response;
        const suggestions = Array.isArray(responseData?.suggestions)
          ? responseData.suggestions
          : Array.isArray(responseData)
            ? responseData
            : [];

        setSuggestionItems(suggestions);

        return response.data;
      }

      setSuggestionItems([]);

      toast.error(
        response?.data?.responseMessage || "Failed to fetch suggestions",
      );

      return null;
    } catch (error) {
      console.error("FETCH CATALOG ITEMS V2 ERROR:", error);

      setSuggestionItems([]);

      toast.error(
        error?.response?.data?.responseMessage || "Something went wrong",
      );

      return null;
    } finally {
      setCatalogFeatureLoading(false);
    }
  };

  // Common update function
  const updateCatalogFeature = async ({
    endpoint,
    payload,
    successMessage,
  }) => {
    try {
      const itemId = String(payload?.id || "").trim();

      if (!itemId) {
        toast.error("Catalog item ID not found");
        return null;
      }

      const response = await api.post(endpoint, payload);

      if (response?.data?.responseCode === "00") {
        toast.success(response?.data?.responseMessage || successMessage);

        return response.data;
      }

      toast.error(response?.data?.responseMessage || "Update failed");

      return null;
    } catch (error) {
      console.error(`CATALOG UPDATE ERROR (${endpoint}):`, error);

      toast.error(
        error?.response?.data?.responseMessage || "Something went wrong",
      );

      return null;
    }
  };

  const updateCatalogRecommended = async ({ id, recommended }) => {
    return updateCatalogFeature({
      endpoint: "/Order/updateCatalogItemsRecommended",

      payload: {
        id: String(id || ""),
        recommended: String(recommended || "0"),
      },

      successMessage: "Recommended status updated successfully",
    });
  };

  const updateCatalogOffers = async ({ id, offers }) => {
    return updateCatalogFeature({
      endpoint: "/Order/updateCatalogItemsOffers",

      payload: {
        id: String(id || ""),
        offers: String(offers || "0"),
      },

      successMessage: "Offer status updated successfully",
    });
  };

  const updateCatalogSuggestion = async ({ id, suggestion }) => {
    return updateCatalogFeature({
      endpoint: "/Order/updateCatalogItemsSuggestion",

      payload: {
        id: String(id || ""),
        suggestion: String(suggestion || "0"),
      },

      successMessage: "Suggestion status updated successfully",
    });
  };

  // =========================================================
  // MESSAGE CONFIGURATION
  // =========================================================

  const getMessageConfig = async (type = 0) => {
    try {
      setMessageConfigLoading(true);

      const finalType = Number(type) === 1 ? 1 : 0;

      console.log("GET MESSAGE CONFIG TYPE:", finalType);

      const response = await api.post(
        `/Chaafo/getMessageConfig?type=${finalType}`,
        "",
      );

      const responseData = response?.data;

      console.log("GET MESSAGE CONFIG RESPONSE:", responseData);

      if (String(responseData?.responseCode) === "00") {
        return {
          ...responseData,
          response: Array.isArray(responseData?.response)
            ? responseData.response
            : [],
        };
      }

      toast.error(
        responseData?.responseMessage ||
          "Failed to fetch message configuration",
      );

      return null;
    } catch (error) {
      console.error(
        "GET MESSAGE CONFIG ERROR:",
        error?.response?.data || error,
      );

      toast.error(
        error?.response?.data?.responseMessage ||
          "Failed to fetch message configuration",
      );

      return null;
    } finally {
      setMessageConfigLoading(false);
    }
  };

  const updateMessageConfig = async ({ messageConfigId, status } = {}) => {
    try {
      const finalMessageConfigId = String(messageConfigId || "").trim();

      if (!finalMessageConfigId) {
        toast.error("Message config ID missing");
        return null;
      }

      const finalStatus =
        String(status || "N")
          .trim()
          .toUpperCase() === "Y"
          ? "Y"
          : "N";

      const requestBody = {
        messageConfigId: finalMessageConfigId,
        status: finalStatus,
      };

      console.log("UPDATE MESSAGE CONFIG PAYLOAD:", requestBody);

      setMessageConfigLoading(true);

      const response = await api.post(
        "/Chaafo/updateMessageConfig",
        requestBody,
      );

      const responseData = response?.data;

      console.log("UPDATE MESSAGE CONFIG RESPONSE:", responseData);

      if (String(responseData?.responseCode) === "00") {
        toast.success(
          responseData?.responseMessage ||
            "Message configuration updated successfully",
        );

        return responseData;
      }

      toast.error(
        responseData?.responseMessage ||
          "Failed to update message configuration",
      );

      return null;
    } catch (error) {
      console.error(
        "UPDATE MESSAGE CONFIG ERROR:",
        error?.response?.data || error,
      );

      toast.error(
        error?.response?.data?.responseMessage ||
          "Failed to update message configuration",
      );

      return null;
    } finally {
      setMessageConfigLoading(false);
    }
  };

  const loginGateway = async ({ number, otp = "", step } = {}) => {
    try {
      const finalNumber = String(number || "").trim();
      const finalOtp = String(otp || "").trim();
      const finalStep = String(step || "").trim();

      if (!finalNumber) {
        toast.error("Mobile number is required");
        return null;
      }

      if (!finalStep) {
        toast.error("Login gateway step is required");
        return null;
      }

      const requestBody = {
        number: finalNumber,
        otp: finalOtp,
        step: finalStep,
      };

      console.log("LOGIN GATEWAY PAYLOAD:", requestBody);

      setLoginGatewayLoading(true);

      const response = await api.post("/Chaafo/logINGateway", requestBody);

      const responseData = response?.data;

      console.log("LOGIN GATEWAY RESPONSE:", responseData);

      if (String(responseData?.responseCode) === "00") {
        return responseData;
      }

      toast.error(
        responseData?.responseMessage || "Login gateway request failed",
      );

      return responseData || null;
    } catch (error) {
      console.error("LOGIN GATEWAY ERROR:", error?.response?.data || error);

      toast.error(
        error?.response?.data?.responseMessage ||
          "Login gateway request failed",
      );

      return error?.response?.data || null;
    } finally {
      setLoginGatewayLoading(false);
    }
  };

  return {
    loading,

    chaafoCategories,
    setChaafoCategories,

    restaurantCategories,
    setRestaurantCategories,

    restaurantsByCategory,
    setRestaurantsByCategory,

    bannerDetails,
    setBannerDetails,

    insertRestaurantViaCategory,
    fetchRestaurantViaCategory,
    updateRestaurantViaCategory,
    deleteRestaurantViaCategory,
    fetchRestaurantCategories,

    insertChaafoCategory,
    fetchChaafoCategories,
    updateChaafoCategory,
    updateChaafoCategorySortOrder,
    deleteChaafoCategory,

    fetchBannerDetails,
    insertBannerDetails,
    updateBannerDetails,
    deleteBannerDetails,
    getChaafoBannerInfo: fetchBannerDetails,
    uploadChaafoImage,

    catalogFeatureLoading,

    catalogMenus,
    recommendedItems,
    offerItems,
    suggestionItems,

    fetchCatalogRecommendedAndOffers,
    fetchCatalogSuggestions,

    updateCatalogRecommended,
    updateCatalogOffers,
    updateCatalogSuggestion,

    messageConfigLoading,
    loginGatewayLoading,
    getMessageConfig,
    updateMessageConfig,
    loginGateway,
    
  };
};;

export default useChaafo;
